import { useFlags } from "launchdarkly-react-client-sdk";

import { BOUNCED_EMAIL_HANDLING_FEATURE } from "constants/feature-flags";
import { useGetPatientQuery } from "services/patients";
import { formatDateOnlyValue } from "utils/common";

// Narrow, flag-gated read: the shared patient queries stay untouched so this
// deploys safely ahead of the backend field (the flag flips only after the
// backend ships — a query for an unknown RestQL field 400s).
const BOUNCE_QUERY = "{id,email_bounced_at,email_bounce_detail}";

export function buildBouncedEmailTooltip(
  bouncedAt: string,
  detail?: string | null
) {
  // The backend renders the timestamp in the practice's timezone, so its first ten
  // characters are the practice's own day — the date the Patients report's Bounced On
  // column shows. `new Date()` re-expressed it in the viewer's zone and could land a day off.
  const bouncedOn = formatDateOnlyValue(bouncedAt.slice(0, 10), "MM/dd/yyyy");
  // Provider messages usually end in a full stop; keep one, not `."."`.
  const reason = detail?.trim().replace(/\.+$/, "");
  return (
    `Emails to this address bounced permanently on ${bouncedOn}` +
    `${reason ? ` — "${reason}"` : ""}. ` +
    `This patient is excluded from email sends. ` +
    `Updating the email address clears this flag.`
  );
}

/**
 * Shared flag-gated bounce lookup for every surface that shows the patient's
 * primary email (profile chip, header contact card, contact modal, appointment
 * form). All consumers use identical query args, so RTK Query collapses their
 * subscriptions into one request per patient.
 */
export function useBouncedEmail(patientId?: number) {
  const flags = useFlags();
  const isEnabled = Boolean(flags[BOUNCED_EMAIL_HANDLING_FEATURE]);

  const { data } = useGetPatientQuery(
    { patientId: patientId as number, query: BOUNCE_QUERY },
    { skip: !isEnabled || !patientId }
  );

  const bouncedAt = isEnabled ? data?.email_bounced_at : undefined;
  const detail = bouncedAt ? data?.email_bounce_detail : undefined;

  return {
    bouncedAt,
    detail,
    tooltip: bouncedAt ? buildBouncedEmailTooltip(bouncedAt, detail) : ""
  };
}
